import moment from "moment";

import { FacturationNetManager } from "@/Billing/Services/FacturationNet/FacturationNetManager";
import { Address } from "@/Common/Api/Contacts/Dto/Address";
import { PhoneNumber } from "@/Common/Api/Contacts/Dto/PhoneNumber";
import { GenericResponse } from "@/Common/Api/Dto/GenericResponse";
import { ExternalBillingClaimDto } from "@/Common/Api/ExternalBilling/Dto/ExternalBillingClaimDto";
import { PreventiveCareNotice } from "@/Common/Api/PreventiveCare/Dto/Responses/PreventiveCareNotice";
import { ArrayHelper } from "@/Common/Helpers/ArrayHelper";
import { NameHelper } from "@/Common/Helpers/NameHelper";
import { PortalHelper } from "@/Common/Helpers/PortalHelper";
import { PrintHelper } from "@/Common/Helpers/PrintHelper";
import { ValidationHelper } from "@/Common/Helpers/ValidationHelper";
import { VisibilityBasedInterval } from "@/Common/Helpers/VisibilityBasedInterval";
import { MyleObjectType } from "@/Common/Models/Enum/MyleObjectType";
import { MyleTabKey } from "@/Common/Models/MyleMenu/enums/MyleTabKey";
import { PortfolioCounters } from "@/Common/Models/MyleMenu/Tooltip/PortfolioCounters";
import { Patient } from "@/Common/Models/Patient/Patient";
import { Person } from "@/Common/Models/Person/Person";
import { PortalLinkedItemType } from "@/Common/Models/Portal/Enums/PortalLinkedItemType";
import { PortalSyncStatus } from "@/Common/Models/Portal/Enums/PortalSyncStatus";
import { LinkedItem } from "@/Common/Models/Portal/SharedItem/LinkedItem";
import { SharedItem } from "@/Common/Models/Portal/SharedItem/SharedItem";
import { SharedItemMessage } from "@/Common/Models/Portal/SharedItem/SharedItemMessage";
import { IPortalLinkedItemModel } from "@/Common/Models/Portal/UI/IPortalLinkedItemModel";
import { IPortalShareableItem } from "@/Common/Models/Portal/UI/IPortalShareableItem";
import { PortfolioInboxes, PreventiveRecallTab } from "@/Common/Models/UI/Enums";
import * as LocalOperations from "@/Common/Operations/operations.local";
import * as ServerOperations from "@/Common/Operations/operations.server";
import * as SubServerOperations from "@/Common/Operations/operations.server.sub";
import { BillingProvider, BillingProviderHelper } from "@/Common/Services/ExternalBilling/BillingProvider";
import { ExternalBillingManager } from "@/Common/Services/ExternalBilling/ExternalBillingManager";
import { BaseAction } from "@/Common/Stores/Actions/BaseAction";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";
import { ServerEvent, ServerEventWithThen } from "@/Common/Stores/Actions/ServerEvent";
import { BaseServerOperationHandler } from "@/Common/Stores/Handlers/Server/BaseServerOperationHandler";
import { ApssClient } from "@/Patient/Api/Apss/ApssClient";
import { PendingApprovalNoteModel } from "@/Patient/Api/Dto/Responses/PendingApprovalNoteModel";
import { FacturationDTO, MedfarBillLightPreview } from "@/Patient/Api/MedfarBilling/Dto/Responses/FacturationReponse";
import { LoadMyNotesResponse } from "@/Portfolio/Api/Dto/Responses/LoadMyNotesResponse";
import { LoadPortfolioCount } from "@/Portfolio/Api/Dto/Responses/LoadPortfolioCountResponse";
import { ConsultationRequestsInboxItem, NotesInboxItem, PendingApprovalsItem, RequestsInboxItem, SentFaxInboxItem } from "@/Portfolio/Models/MyNotesInboxItem";
import { MyConsultationRequestsTableItem, MyRequestsTableItem, MySentFaxTableItem, NotesInboxTableItem, PendingApprovalsTableItem, PreventiveActivitiesTableItem, PreventiveRecallListTableItem } from "@/Portfolio/Models/MyNotesInboxTableItem";
import { PortalSyncHistoryTableItem, SharedItemHistoryDto } from "@/Portfolio/Models/PortalSyncHistory";
import { ISentFaxInboxModel } from "@/Portfolio/Models/UI/ISentFaxInboxModel";
import { PortfolioStore } from "@/Portfolio/Stores/PortfolioStore";

class PortfolioResponseHandler extends BaseServerOperationHandler {
    constructor(action: BaseAction) {
        super(action);

        this.RegisterOperation(ServerOperations.Server.Myle_LoadMyNotesData, this.LoadMyNotesData);
        this.RegisterOperation(ServerOperations.Server.Myle_LoadMyConsultationRequestsData, this.LoadMyConsultationRequestsData);
        this.RegisterOperation(ServerOperations.Server.Myle_LoadExamRequests, this.LoadMyRequestsData);
        this.RegisterOperation(ServerOperations.Server.Myle_LoadMySentFaxData, this.loadMySentFaxData);
        this.RegisterOperation(ServerOperations.Server.Myle_Faxing_GetSentFaxStatus, this.refreshSentFaxStatus);
        this.RegisterOperation(ServerOperations.Server.Myle_MyNotesGeneratePDF, this.MyNotesGeneratePDF);
        this.RegisterOperation(ServerOperations.Server.Myle_LoadPatient, this.LoadPatientConsultationRequest, SubServerOperations.Portfolio.LoadPatienrConsultationRequest);
        this.RegisterOperation(ServerOperations.Server.Myle_LoadPatient, this.LoadPatientRequest, SubServerOperations.Portfolio.LoadPatientRequest);
        this.RegisterOperation(ServerOperations.Server.Myle_DeleteLabRequest, this.DeleteSelectedRequest, SubServerOperations.Portfolio.DeleteRequest);
        this.RegisterOperation(ServerOperations.Server.Myle_PrintSelectedRequest, this.PrintSelectedRequest_ForIFrameRerender, SubServerOperations.Portfolio.GenerateLabRequest_ForIFrameReRender);
        this.RegisterOperation(ServerOperations.Server.Myle_PrintSelectedRequest, this.PrintSelectedRequest, SubServerOperations.Portfolio.GenerateLabRequest_ForPrint);
        this.RegisterOperation(ServerOperations.Server.Myle_CountPortfolios, this.LoadPortfolioCount);
        this.RegisterOperation(ServerOperations.Server.MedfarBilling_GetBill, this.LoadMedfarBillingForNotes);
        this.RegisterOperation(ServerOperations.Server.Myle_Faxing_ResendFax, this.resendFax);
        this.RegisterOperation(ServerOperations.Server.Myle_Faxing_ResendFaxWithChanges, this.resendFaxWithChanges);
        this.RegisterOperation(ServerOperations.Server.Myle_Portfolio_ToggleFailedFaxVisibilty, this.ToggleFailedFaxVisibility);
        this.RegisterOperation(ServerOperations.Server.Myle_Resident_GetPendingApprovals, this.GetPendingApprovals);
        this.RegisterOperation(ServerOperations.Server.Myle_Patient_MedicalNote_GenerateNotePDF, this.GeneratePendingApprovalNotePDF);
        this.RegisterOperation(ServerOperations.Server.Myle_Resident_LoadPendingApprovalNotes, this.LoadPendingApprovalNotes);
        this.RegisterOperation(ServerOperations.Server.Myle_Resident_SendPendingApproval, this.SendPendingApproval);
        this.RegisterOperation(ServerOperations.Server.Myle_Resident_ApprovePendingApproval, this.ApprovePendingApproval);
        this.RegisterOperation(ServerOperations.Server.Myle_Resident_ReturnPendingApproval, this.ReturnPendingApproval);
        this.RegisterOperation(ServerOperations.Server.Myle_Faxing_SendConsultationRequest, this.FaxConsultationRequest, SubServerOperations.Portfolio.FaxConsultationRequest);
        this.RegisterOperation(ServerOperations.Server.Portfolio_GetSentFaxPreview, this.getSentFaxPreview);
        this.RegisterOperation(ServerOperations.Server.Myle_UploadApssAttachments, this.Myle_UploadApssAttachments, SubServerOperations.Portfolio.APSSUploadAttachement);
        this.RegisterOperation(ServerOperations.Server.Myle_UploadApssAttachments, this.Myle_UploadApssAttachments_ActionRequired, SubServerOperations.Portfolio.APSSUploadAttachementActionRequired);
        this.RegisterOperation(ServerOperations.Server.Myle_Faxing_SendLabRequest, this.faxLabRequest);
        this.RegisterOperation(ServerOperations.Server.Myle_Portfolio_LoadPortalPendingItems, this.loadPortalSyncHistory);
        this.RegisterOperation(ServerOperations.Server.Myle_Portfolio_LoadPortalSyncHistory, this.loadPortalSyncHistory);
        this.RegisterOperation(ServerOperations.Server.Myle_Patient_Portal_GetSharedItem, this.loadSharedItem, SubServerOperations.PortalLinkedItemStatus.Portfolio);

        this.RegisterOperation(ServerOperations.Server.Myle_Patient_Portal_SyncSharedItem, this.updateSharedItem);
        this.RegisterOperation(ServerOperations.Server.Myle_Patient_Portal_RevokeSharedItem, this.updateSharedItem);
        this.RegisterOperation(ServerOperations.Server.Myle_Portfolio_LoadMyPreventiveActivities, this.loadPreventiveActivities);
        this.RegisterOperation(ServerOperations.Server.Myle_Portfolio_PreventiveCare_LoadRecallList, this.loadPreventiveRecallList);
        this.RegisterOperation(ServerOperations.Server.Myle_Portfolio_PreventiveCare_GenerateLetters, this.generateLetters);
        this.RegisterOperation(ServerOperations.Server.Myle_Portfolio_PreventiveCare_GenerateBonus, this.generateBonusReport);
        this.RegisterOperation(ServerOperations.Server.Myle_Portfolio_PreventiveCare_LoadNotices, this.loadPreventiveCareNotices);
    }

    private Myle_UploadApssAttachments(): void {
        window.setTimeout(
            () => {
                LocalEventDispatcher.dispatch(
                    new LocalEvent(LocalOperations.Portfolio.APSS_Reload)
                );
            },
            50
        );
    }

    private ToggleFailedFaxVisibility(response: ServerEvent, store: PortfolioStore): void {
        let model = store.getActiveFaxModel();
        const data = response.Data;

        if (data.IsSuccess === true) {
            let tableData = model.get("TableData");

            const updatedList = tableData
                .map(x => {
                    if (x.Model.SentFaxId == data.SentFaxId && x.Model.ItemId == data.ItemId)
                        x.Model.FailCounterVisibility = data.Value;
                    return x;
                });

            model = model.set("TableData", Immutable.List<MySentFaxTableItem>(updatedList));

            store.PortfolioActions.LoadPortfolioCount(); // refresh count

            if (cacheStore.ClinicSettings.EnableFaxSendingIndicator) {
                patientStore.ActionsMedicalNote.loadSentFaxes([data.ItemId]);
            }
        }

        store.setActiveFaxModel(model);
    }

    private LoadMedfarBillingForNotes(response: ServerEvent, store: PortfolioStore): void {
        let responseModel: FacturationDTO[] = response.Data.MedfarBillingList;

        if (response.Data.Has500Patients != null)
            sessionStore.has500patient = response.Data.Has500Patients;

        if (responseModel == null) responseModel = [];
        let data = store.NotesInboxModel.get("TableData").toArray();
        let length = responseModel.length;

        data.map(n => {
            n.Model.LightMedfarBillsList = [];

            for (let i = 0; i < length; i++) {
                if (n.Model.EncounterId === responseModel[i].EncounterId) {
                    let item = new MedfarBillLightPreview();

                    item.FacturationRequestId = responseModel[i].FacturationRequestID;
                    item.CreationDate = responseModel[i].CreationDate;
                    item.BillDate = responseModel[i].DateFacturation;
                    item.IsEditable = false;

                    item.BillSummary = "";
                    if (responseModel[i].MedfarBillingStatus)
                        item.BillSummary = responseModel[i].MedfarBillingStatus;

                    if (responseModel[i].FacturationCodes && responseModel[i].FacturationCodes.length > 0)
                        item.BillSummary += ` ( ${responseModel[i].FacturationCodes.map(p => p.CodeFacturation).join(", ")} )`;

                    n.Model.LightMedfarBillsList.push(item);
                }
            }
            return n;
        });

        store.NotesInboxModel = store.NotesInboxModel.set("TableData", Immutable.List(data));
    }

    private MyNotesGeneratePDF(response: ServerEvent): void {
        PrintHelper.ShowPrintPreviewFileName(response.Data.FileName);
    }

    private async LoadMyNotesData(response: ServerEvent, store: PortfolioStore): Promise<void> {
        let responseModel: LoadMyNotesResponse = response.Data;
        let sendingEncounterIds: string[] = [];

        let tableItems = responseModel.Result.map(x => {
            let itemModel: NotesInboxItem = new NotesInboxItem();
            itemModel.EncounterId = x.EncounterId;
            itemModel.ContactId = x.ContactId;
            itemModel.PatientName = NameHelper.formatFullNameForHasPatientName(x);
            itemModel.EncounterDate = x.ConsultationDate != null ? moment(x.ConsultationDate).toDate() : null;
            itemModel.EmployeeIds = x.EmployeeIds;
            itemModel.PatientId = x.PatientId;
            itemModel.IsAccessProhibited = x.IsAccessProhibited;

            sendingEncounterIds.push(itemModel.EncounterId);

            if (x.Status == "0")
                itemModel.Status = cacheStore.GetString("PendingLabel");
            else if (x.Status == "1")
                itemModel.Status = cacheStore.GetString("ReturnedLabel");
            else if (x.Status == "2" || x.IsSignedOff)
                itemModel.Status = cacheStore.GetString("ApprovedLabel");
            else if (x.Status == "3")
                itemModel.Status = cacheStore.GetString("NotApprovedLabel");

            itemModel.VisitPurpose = x.VisitPurpose;

            itemModel.Diagnosis = (x.Diagnosis || []).join(";");
            itemModel.HealthCardNumber = x.NAM;
            itemModel.FacturationNetBillsList = x.FacturationNetInvoices;
            itemModel.ExternalBillingClaims = x.ExternalBillingClaims;

            return new NotesInboxTableItem(itemModel);
        });
        store.NotesInboxModel = store.NotesInboxModel
            .set("TotalQueryPages", response.Data.TotalQueryPages)
            .set("TotalQueryRows", response.Data.TotalQueryRows)
            .set("CurrentPage", response.Data.CurrentPage)
            .set("TableData", Immutable.List<NotesInboxTableItem>(tableItems));

        if (sessionStore.getCurrentEmployee().isActiveSiteMexico() === false)
            store.PortfolioActions.GetNotesMedfarBills(sendingEncounterIds);

        store.PortfolioActions.LoadPortfolioCount();

        this.updateInvoiceStatuses(store);
    }

    private LoadMyConsultationRequestsData(response: ServerEvent, store: PortfolioStore): void {
        let tableItems = response.Data.Result?.map(x => {
            const requestingStaff = cacheStore.GetEmployeeByContactFacetId(x.RequestingStaffContactFacetId);
            const requestingStaffTitle = requestingStaff ? cacheStore.GetLookupById(requestingStaff.PrefixLookupId) : null;

            const itemModel = new ConsultationRequestsInboxItem({
                AttendingDoctorsName: requestingStaff ? `${requestingStaffTitle?.GetValue() ?? ""} ${requestingStaff?.LastName}, ${requestingStaff?.FirstName}`.trim() : "",
                DoctorLicenseNumber: requestingStaff?.FirstLicenceNumber,
                PatientName: NameHelper.formatFullNameForHasPatientName(x),
                ConsultationRequestId: x.ConsultationRequestId,
                Speciality: x.Speciality,
                Recipient: x.Recipient,
                ConsultationRequestDate: x.ConsultationRequestDate,
                SiteId: x.SiteId,
                Description: x.Description,
                PatientId: x.PatientId,
                IsSignedOff: x.IsSignedOff,
                IsUrgent: x.IsUrgent,
                IsFaxed: x.IsFaxed,
                ContactId: x.ContactId,
                EncounterId: x.EncounterId,
                IsPrinted: x.IsPrinted,
                SupervisorContactId: x.SupervisorContactId,
                CostCenterId: x.CostCenterId,
                PriorityDropdownId: x.PriorityDropdownId,
                Status: x.StatusLookupId,
                DueDate: x.DueDate,
                RequestingStaffContactFacetId: x.RequestingStaffContactFacetId,
                CreatedByContactId: x.CreatedByContactId,
                IsAccessProhibited: x.IsAccessProhibited,
                ReferralConsultationDate: x.ReferralConsultationDate,
                PatientNextAppointmentDateTime: x.PatientNextAppointmentDateTime,
                HasAppointmentToday: x.HasAppointmentToday,
                PatientPortalSyncState: x.PatientPortalSyncState,
                SharedItem: x.SharedItem,
                SharedItemId: x.SharedItemId,
                SignedPrintAuthorizations: x.SignedPrintAuthorizations,
                Comments: x.Comments,
                FirstPrintedByEmployeeId: x.FirstPrintedByEmployeeId,
                FirstPrintedDate: x.FirstPrintedDate,
                SignedOffDate: x.SignedOffDate,
                SignedOffByEmployeeId: x.SignedOffByEmployeeId
            });

            return new MyConsultationRequestsTableItem(itemModel);
        });

        store.ConsultationRequestsInboxModel = store.ConsultationRequestsInboxModel
            .set("TotalQueryPages", response.Data.TotalQueryPages)
            .set("TotalQueryRows", response.Data.TotalQueryRows)
            .set("CurrentPage", response.Data.CurrentPage)
            .set("TableData", Immutable.List<MyConsultationRequestsTableItem>(tableItems));
    }

    private LoadMyRequestsData(response: ServerEvent, store: PortfolioStore): void {
        let tableItems = response.Data.Result.map(x => {
            let itemModel = new RequestsInboxItem();

            itemModel.PatientName = NameHelper.formatFullNameForHasPatientName(x);
            itemModel.RequestId = x.RequestId;
            itemModel.Reason = x.Reason;
            itemModel.RequestDate = x.RequestDate;
            itemModel.PatientId = x.PatientId;
            itemModel.SiteId = x.SiteId;
            itemModel.IsSignedOff = x.IsSignedOff;
            itemModel.EncounterId = x.EncounterId;
            itemModel.ContactId = x.ContactId;
            itemModel.PriorityDropdownId = x.PriorityDropdownId;
            itemModel.RequestingStaffContactFacetId = x.RequestingStaffContactFacetId;
            itemModel.DueDate = x.DueDate;
            itemModel.CreationDate = x.CreationDate;
            itemModel.CreatedByContactId = x.CreatedByContactId;
            itemModel.StatusLookupId = x.StatusLookupId;
            itemModel.Request = x.Request;
            itemModel.IsAccessProhibited = x.IsAccessProhibited;

            return new MyRequestsTableItem(itemModel);
        });

        store.RequestsInboxModel = store.RequestsInboxModel
            .set("TotalQueryPages", response.Data.TotalQueryPages)
            .set("TotalQueryRows", response.Data.TotalQueryRows)
            .set("CurrentPage", response.Data.CurrentPage)
            .set("TableData", Immutable.List<MyRequestsTableItem>(tableItems));
    }

    private loadMySentFaxData(response: ServerEvent, store: PortfolioStore): void {
        if (response.hasErrors()) {
            sessionStore.showPopup(response.Errors);
            return;
        }

        let tableItems: MySentFaxTableItem[] = response.Data.SentFaxes
            .map(x => {
                let itemModel: SentFaxInboxItem = new SentFaxInboxItem();
                let employee = cacheStore.GetEmployeeByContactFacetId(x.SenderEmployeeId);
                let employeeTitle = cacheStore.GetLookupById(employee.PrefixLookupId);
                const prefix = employeeTitle != null ? employeeTitle.GetValue() : "";
                let patientName = NameHelper.formatFullNameForHasPatientName(x);

                itemModel.SentBy = `${prefix} ${employee.LastName}, ${employee.FirstName}`;
                itemModel.SentFaxId = x.Id;
                itemModel.PatientName = !IsNullOrWhiteSpace(patientName) ? patientName : "--";
                itemModel.ItemTypeName = x.SentFaxItemTypeName;
                itemModel.RecipientName = x.RecipientName;
                itemModel.RecipientFax = x.RecipientFax;
                itemModel.DateSent = x.SentDate;
                itemModel.ReceivedDate = x.ReceiveDate;
                itemModel.Status = x.SentStatusName;
                itemModel.ItemDate = x.SentFaxItemCreationDate;
                itemModel.ItemType = x.SentFaxItemTypeId;
                itemModel.ItemId = x.SentFaxItemId;
                itemModel.SentFaxFiles = x.SentFaxFiles;
                itemModel.FailCounterVisibility = x.FailCounterVisibility;
                itemModel.SentStatus = x.SentStatus;
                itemModel.SenderEmployeeId = x.SenderEmployeeId;
                itemModel.PatientId = x.PatientId;
                itemModel.PatientContactId = x.PatientContactId;
                itemModel.RequestingStaffContactFacetId = x.RequestingStaffContactFacetId;
                itemModel.ProviderName = x.ProviderName;
                itemModel.ProviderError = x.ProviderError;
                itemModel.Retries = x.Retries;
                itemModel.NextRetryDate = x.NextRetryDate ? new Date(x.NextRetryDate) : null;
                itemModel.HasCoverPage = x.HasCoverPage;

                return new MySentFaxTableItem(itemModel);
            });

        let model = store.getActiveFaxModel();

        model = model
            .set("TotalQueryPages", response.Data.TotalQueryPages)
            .set("TotalQueryRows", response.Data.TotalQueryRows)
            .set("CurrentPage", response.Data.CurrentPage)
            .set("SelectedPageNumber", response.Data.CurrentPage)
            .set("TableData", Immutable.List<MySentFaxTableItem>(tableItems));

        let ids = tableItems.map(x => x.Model.SentFaxId);
        store.PortfolioActions.RefreshSentFaxStatuses(ids);

        store.setActiveFaxModel(model);
    }

    private refreshSentFaxStatus(response: ServerEvent, store: PortfolioStore): void {
        let model = store.getActiveFaxModel();
        let tableData = model.get("TableData").toArray();
        let statuses = response.Data.Statuses;

        for (let x of tableData) {
            let status = statuses.firstOrDefault(stat => stat.Id == x.Model.SentFaxId);
            if (status != null)
                x.Model.Status = cacheStore.GetSentFaxSentStatusName(status.Status);
        }

        model.set("TableData", Immutable.List(tableData));
        store.setActiveFaxModel(model);
    }

    private LoadPatientConsultationRequest(response: ServerEvent, store: PortfolioStore): void {
        let patient = Patient.FromResponse(response);
        let healthCardNumber = patient && patient.HealthCards && patient.HealthCards[0]
            ? patient.HealthCards[0].Number
            : "";
        let genderLookup = cacheStore.GetLookupById(patient.IndividualResponse.GenderLookupId);
        let phone = this.GetPatientPhoneNumber(patient.Phones);

        let phoneStr = "";
        if (phone) {
            let phonetype = `(${cacheStore.GetLocalizedValueByLookupId(phone.ContactPointTypeLookupId)}-${cacheStore.GetLocalizedValueByLookupId(phone.PhoneTypeLookupId)})`;
            phoneStr = `+1 (${phone.Number.substr(0, 3)}) ${phone.Number.substr(3, 3)} ${phone.Number.substr(6, 4)} ${phonetype}`;
        }

        store.ConsultationRequestPopupDataModel = store.ConsultationRequestPopupDataModel
            .set("PatientName", NameHelper.formatFullNameForHasName(patient.IndividualResponse))
            .set("PatientNAM", healthCardNumber)
            .set("PatientExpDate", patient.GetFormattedNAMExpirationDateString())
            .set("PatientFileNumber", patient.PatientResponse.Identifier01)
            .set("PatientGender", genderLookup.GetValue())
            .set("PatientDateOfBirth", moment(patient.IndividualResponse.DateOfBirth).format("YYYY/MM/DD"))
            .set("PatientPhone", phoneStr)
            .set("PatientClinicians", patient.ToPatientCliniciansModel())
            .set("Patient", patient);
    }

    private LoadPatientRequest(response: ServerEvent, store: PortfolioStore): void {
        let patient = Patient.FromResponse(response);
        let healthCardNumber = patient && patient.HealthCards && patient.HealthCards[0]
            ? patient.HealthCards[0].Number
            : "";
        const validationHelper = new ValidationHelper();
        const address = this.GetPatientAddress(patient.Addresses);
        const genderLookup = cacheStore.GetLookupById(patient.IndividualResponse.GenderLookupId);
        const phone = this.GetPatientPhoneNumber(patient.Phones);

        store.RequestPopupDataModel = store.RequestPopupDataModel
            .set("PatientName", NameHelper.formatFullNameForHasName(patient.IndividualResponse))
            .set("PatientNAM", healthCardNumber)
            .set("PatientExpDate", patient.GetFormattedNAMExpirationDateString())
            .set("PatientFileNumber", patient.PatientResponse.Identifier01)
            .set("PatientGender", genderLookup.GetValue())
            .set("PatientDateOfBirth", moment(patient.IndividualResponse.DateOfBirth).format("YYYY/MM/DD"));

        if (address) {
            store.RequestPopupDataModel = store.RequestPopupDataModel
                .set("PatientStreetAddress", address.StreetAddress)
                .set("PatientState", cacheStore.GetLookupById(address.StateLookupId).GetValue())
                .set("PatientCountry", cacheStore.GetLookupById(address.CountryLookupId).GetValue())
                .set("PatientZipCode", validationHelper.getFormattedPostalCodeString(address.ZipCode, address.CountryLookupId, address.StateLookupId))
                .set("PatientCity", address.City);
        }
        else {
            store.RequestPopupDataModel = store.RequestPopupDataModel
                .set("PatientStreetAddress", "")
                .set("PatientState", "")
                .set("PatientCountry", "")
                .set("PatientZipCode", "")
                .set("PatientCity", "");
        }

        if (phone) {
            store.RequestPopupDataModel = store.RequestPopupDataModel
                .set("PatientPhoneNumber", validationHelper.getFormattedPhoneNumberString(phone.Number.toString(), phone.CountryLookupId, phone.StateLookupId));
        }

        store.RequestsInboxModel = store.RequestsInboxModel
            .set("PatientId", patient.IndividualResponse.Contact_id);
    }

    private GetPatientPhoneNumber(phones: PhoneNumber[]): PhoneNumber {
        return phones && phones.firstPreferredOrDefault(x => x.IsPreferred);
    }

    private GetPatientAddress(addresses: Address[]): Address {
        return addresses && addresses.firstPreferredOrDefault(x => x.IsPreferred);
    }

    private DeleteSelectedRequest(response: ServerEvent, store: PortfolioStore): void {
        if (response.hasErrors()) {
            sessionStore.showPopup({
                title: cacheStore.GetString("WarningLabel"),
                content: cacheStore.GetString(response.Errors[0].Code),
                showCancel: true
            });
            return;
        }

        store.RequestsInboxModel = store.RequestsInboxModel.set("RequestPopupIsVisible", false);
        store.PortfolioActions.LoadMyRequests(store.RequestsInboxModel);
        sessionStore.hideGlobalOverlay();
    }

    private PrintSelectedRequest(response: ServerEvent, store: PortfolioStore): void {
        if (response.Data.RequestId) {
            store.RequestsInboxModel = store.RequestsInboxModel.set("RequestPopupIsVisible", false);
            let labRequests = store.RequestsInboxModel.get("TableData").toArray();

            let foundLab = labRequests.filter(x => x.Model.RequestId == response.Data.RequestId).map(p => p.Model).firstOrDefault();
            if (foundLab) {
                foundLab.IsPrinted = true;
                store.RequestsInboxModel = store.RequestsInboxModel.set("TableData", Immutable.List(labRequests));
            }
            PrintHelper.ShowPrintPreviewFileName(response.Data.FileName);
        }
        sessionStore.hideGlobalOverlay();
    }

    private PrintSelectedRequest_ForIFrameRerender(response: ServerEvent, store: PortfolioStore): void {
        store.RequestsInboxModel = store.RequestsInboxModel.set("RequestPopupIsVisible", false);
        store.RequestsInboxModel = store.RequestsInboxModel.set("IsLabFaxPopupOpen", true);

        store.RequestsInboxModel = store.RequestsInboxModel.set("LabRequestPreviewPDFURLFileName", response.Data.FileName);
        store.RequestsInboxModel = store.RequestsInboxModel.set("LabRequestPreviewPDFURL", PrintHelper.GetPreviewPath(response.Data.FileName));

        let labRequests = store.RequestsInboxModel.get("TableData").toArray();

        let foundLab = labRequests.filter(x => x.Model.RequestId == response.Data.RequestId).map(p => p.Model).firstOrDefault();
        if (foundLab) {
            foundLab.IsPrinted = true;
            if (response.Data.HasSignature)
                foundLab.IsFaxed = true;
        }

        store.RequestsInboxModel = store.RequestsInboxModel.set("TableData", Immutable.List(labRequests));
        sessionStore.hideGlobalOverlay();
    }

    private faxLabRequest(response: ServerEvent, store: PortfolioStore): void {
        let faxResponse: GenericResponse = response.Data;
        store.RequestsInboxModel = store.RequestsInboxModel
            .set("IsLabFaxPopupOpen", false)
            .set("RequestId", null)
            .set("LabRequestFaxResponse", faxResponse);

        sessionStore.showGlobalOverlay(true);
    }

    private async LoadPortfolioCount(response: ServerEvent, store: PortfolioStore): Promise<void> {
        const r: LoadPortfolioCount = response.Data;
        const urgentPortfolioCount = r.SupervisionNoteCount + r.FailedFaxesCount + r.FailedSharedItemsCount;

        const conseilNumeriqueCount = store.Model.get("ApssConseilNumeriqueCount") ?? 0;
        const actionRequiredCount = store.Model.get("ApssActionRequiredCount") ?? 0;

        const execute = async (): Promise<void> => {
            if (!cacheStore.ClinicSettings.EnableConseilNumerique)
                return;

            const countConseilNumeriqueResponse = await ApssClient.post(ApssClient.getAPSSRequestCount(true, false));
            const conseilNumeriqueCountFromResponse = countConseilNumeriqueResponse?.ReqDisplayTotal as number ?? 0;

            const actionRequiredCountNumeriqueResponse = await ApssClient.post(ApssClient.getAPSSRequestCount(false, true));
            const actionRequiredCountFromResponse = actionRequiredCountNumeriqueResponse?.ReqDisplayTotal as number ?? 0;

            portfolioStore.Model = portfolioStore.Model.set("ApssConseilNumeriqueCount", conseilNumeriqueCountFromResponse);
            portfolioStore.Model = portfolioStore.Model.set("ApssActionRequiredCount", actionRequiredCountFromResponse);

            // put the counter inside the interval too
            const portfolioCounters = new PortfolioCounters(
                response.Data.PortfolioCount,
                response.Data.SupervisionNoteCount,
                response.Data.FailedFaxesCount,
                response.Data.FailedSharedItemsCount,
                conseilNumeriqueCountFromResponse,
                actionRequiredCountFromResponse
            );

            cacheStore.UpdateTab(
                MyleTabKey.Portfolio,
                r.PortfolioCount,
                urgentPortfolioCount,
                conseilNumeriqueCountFromResponse,
                actionRequiredCountFromResponse,
                false,
                { portfolioCounters }
            );
        };

        if (sessionStore.hasCrdsAccess() && cacheStore.ClinicSettings.EnableConseilNumerique) {
            // once this was executed once, don't auto execute it everytime. Only every 30min.
            if (!store.conseilNumeriqueInterval) {
                store.conseilNumeriqueInterval = new VisibilityBasedInterval(
                    () => execute(),
                    1800000, // 30 minutes
                    { executeOnInitialization: true }
                );
            }
        }

        const portfolioCounters = new PortfolioCounters(
            response.Data.PortfolioCount,
            response.Data.SupervisionNoteCount,
            response.Data.FailedFaxesCount,
            response.Data.FailedSharedItemsCount,
            conseilNumeriqueCount,
            actionRequiredCount
        );

        // update top menu
        cacheStore.UpdateTab(
            MyleTabKey.Portfolio,
            r.PortfolioCount,
            urgentPortfolioCount,
            conseilNumeriqueCount,
            actionRequiredCount,
            false,
            { portfolioCounters }
        );

        store.Model = store.Model
            .set("PortfolioCount", r.PortfolioCount)
            .set("SupervisionNoteCount", r.SupervisionNoteCount)
            .set("FailedFaxesCount", r.FailedFaxesCount)
            .set("FailedSharedItemsCount", r.FailedSharedItemsCount)
            .set("ApssConseilNumeriqueCount", conseilNumeriqueCount)
            .set("ApssActionRequiredCount", actionRequiredCount);
    }

    private resendFax(response: ServerEvent, store: PortfolioStore): void {
        let model = this.updateFaxStatuses(response.Data, store.getActiveFaxModel());
        store.setActiveFaxModel(model);
    }

    private resendFaxWithChanges(response: ServerEvent, store: PortfolioStore): void {
        let model = this.updateFaxStatuses(response.Data, store.getActiveFaxModel());
        store.setActiveFaxModel(model);
    }

    private updateFaxStatuses(data: any, model: ISentFaxInboxModel): any {
        if (data.Success === true) {
            let tableData = model.get("TableData");

            let updatedList = tableData.map(x => {
                if (data.SentFaxIds.some(z => z === x.Model.SentFaxId)) {
                    x.Model.Status = cacheStore.GetString("InProgressLabel");
                }
                return x;
            });

            return model.set("TableData", Immutable.List<MySentFaxTableItem>(updatedList));
        }

        return model;
    }

    private GetPendingApprovals(response: ServerEvent, store: PortfolioStore): void {
        let data: PendingApprovalsItem[] = response.Data["PendingApprovalList"];

        if (data && data.length > 0) {
            let tableItems = data.map(item => new PendingApprovalsTableItem(item));
            store.PendingApprovalInboxModel = store.PendingApprovalInboxModel
                .set("TableData", Immutable.List<PendingApprovalsTableItem>(tableItems));

            if (response.Data["IsAfterAction"]) {
                store.PortfolioActions.GeneratePendingApprovalNotePDF(data[0].EncounterId, data[0].PatientContactId);
                store.PendingApprovalInboxModel = store.PendingApprovalInboxModel
                    .set("SelectedPendingApprovalPatientContactId", data[0].PatientContactId)
                    .set("SelectedPendingApprovalSupervisorContactId", data[0].SupervisorContactId);
            }
        }
        else {
            store.PendingApprovalInboxModel = store.PendingApprovalInboxModel
                .set("TableData", Immutable.List<PendingApprovalsTableItem>([]))
                .set("HasNotePDF", false);
        }
        store.PortfolioActions.LoadPortfolioCount();
    }

    private GeneratePendingApprovalNotePDF(response: ServerEvent, store: PortfolioStore): void {
        if (response.Data["FileName"]) {
            store.PendingApprovalInboxModel = store.PendingApprovalInboxModel
                .set("NoteFileName", response.Data["FileName"])
                .set("HasNotePDF", true)
                .set("SelectedPendingApprovalEncounterId", response.Data["EncounterId"])
                .set("PdfNoteOpen", true);
        }
        store.PortfolioActions.LoadPortfolioCount();
    }

    private LoadPendingApprovalNotes(response: ServerEvent, store: PortfolioStore): void {
        let notesList: PendingApprovalNoteModel[] = response.Data["NotesList"];
        let encounterId: string = response.Data["EncounterId"];
        let patientContactId: string = response.Data["PatientContactId"];

        if (notesList && notesList.length > 0) {
            let clinician = store.PendingApprovalInboxModel
                .get("ApprovalSupervisorClinicians")
                .toArray()
                .firstOrDefault(c => c.ContactId === notesList[0].SupervisorContactId);
            if (clinician) {
                let employee = cacheStore.GetEmployeeByContactIdOrEmpty(clinician.ContactId);
                let employeeTitle = cacheStore.GetLookupById(employee.PrefixLookupId);

                store.PendingApprovalInboxModel = store.PendingApprovalInboxModel
                    .set("ApprovalSupervisorClinicianName", `${employeeTitle.GetValue()} ${employee.LastName}, ${employee.FirstName}`);
            }

            store.PendingApprovalInboxModel = store.PendingApprovalInboxModel
                .set("ApprovalSupervisorSelectedClinicians", Immutable.List<Person>(clinician ? [clinician] : []))
                .set("PendingNoteNotesList", Immutable.List(notesList))
                .set("PendingNotesPopup", true);
        }
        else {
            store.PendingApprovalInboxModel = store.PendingApprovalInboxModel
                .set("PendingNoteNotesList", Immutable.List([]));
        }

        // ???? This prevents us from calling load notes without triggering a reload universe. To be reviewed.
        store.PortfolioActions.GeneratePendingApprovalNotePDF(encounterId, patientContactId);
        store.PortfolioActions.LoadPortfolioCount();
    }

    public SendPendingApproval(response: ServerEvent, store: PortfolioStore): void {
        store.PendingApprovalInboxModel = store.PendingApprovalInboxModel
            .set("PendingNotesPopup", false)
            .set("ApprovalResidentMessage", "");

        store.PortfolioActions.LoadPendingApproval(store.PendingApprovalInboxModel);
        store.PortfolioActions.LoadPortfolioCount();
    }

    private ApprovePendingApproval(response: ServerEventWithThen<any, (success: boolean) => void>, store: PortfolioStore): void {
        let succesfullyApproved = response.Data["Success"] && !response.hasErrors();
        if (!succesfullyApproved) {
            sessionStore.showPopup(response.Errors);
        }
        else {
            store.PendingApprovalInboxModel = store.PendingApprovalInboxModel
                .set("PendingNotesPopup", false)
                .set("ReturnPendingApprovalMessage", "");

            store.PortfolioActions.LoadPendingApproval(store.PendingApprovalInboxModel, true);
            store.PortfolioActions.LoadPortfolioCount();
        }

        response.then?.(succesfullyApproved);
    }

    private ReturnPendingApproval(response: ServerEvent, store: PortfolioStore): void {
        if (response.Data["Success"]) {
            store.PendingApprovalInboxModel = store.PendingApprovalInboxModel
                .set("PendingNotesPopup", false)
                .set("ReturnPendingApprovalMessage", "");

            store.PortfolioActions.LoadPendingApproval(store.PendingApprovalInboxModel, true);
            store.PortfolioActions.LoadPortfolioCount();
        }
    }

    private FaxConsultationRequest(response: ServerEvent, store: PortfolioStore): void {
        let faxResponse: GenericResponse = response.Data;

        store.ConsultationRequestsInboxModel = store.ConsultationRequestsInboxModel
            .set("IsConsultationRequestFaxPopupOpen", false)
            .set("ConsultationRequestFaxResponse", faxResponse);

        sessionStore.showGlobalOverlay(true);
    }

    private getSentFaxPreview(response: ServerEvent, store: PortfolioStore): void {
        let previewPDF = response.Data.FaxPreviewURL;
        let sentFaxItem: SentFaxInboxItem = response.Data.SentFaxItem;
        let isPermanentFile: boolean = response.Data.IsPermanentFaxMessageFilePath;
        let model = store.getActiveFaxModel();

        model = model.set("SentFaxPDF", previewPDF)
            .set("SelectedSentFaxInboxItem", sentFaxItem)
            .set("IsPermanentFaxMessagePath", isPermanentFile);

        store.setActiveFaxModel(model);
    }

    // TODO - remove copy-pasta, merge facturation net into provider interfaces
    // code is mostly duplicated in PatientMedicalNoteResponseHandler.ts
    private updateInvoiceStatuses(store: PortfolioStore): void {
        const resolvedProvider = sessionStore.getCurrentEmployee().getBillingProviderResolved();

        if (BillingProviderHelper.shouldUseLegacyMedfarBilling(resolvedProvider)) {
            return;
        }

        if (BillingProviderHelper.shouldUseLegacyFacturationNetBilling(resolvedProvider)) {
            this.updateFacturationNetInvoiceStatuses(store);
            return;
        }

        this.updateExternalBillingClaimStatusesAsync(store);
    }

    private updateFacturationNetInvoiceStatuses(store: PortfolioStore): void {
        if (sessionStore.getCurrentEmployee().getBillingProviderResolved() != BillingProvider.FacNet)
            return;

        let encounters = store.NotesInboxModel.get("TableData").toArray();

        // get invoice IDs to update
        let invoiceIds = encounters
            .flatten(e => e.Model.FacturationNetBillsList)
            .filter(bill => !FacturationNetManager.isInvoiceStatusClosed() && bill.CreatedByEmployeeId === sessionStore.getCurrentEmployee().ContactFacetId)
            .map(bill => parseInt(bill.ClaimId));
        if (!invoiceIds.length)
            return;

        // fetch updated invoices
        FacturationNetManager.getUpdatedClaimStatuses(
            invoiceIds,
            (updatedClaims, deletedIds, errors) => {
                if (errors && errors.length)
                    return;

                let updatedClaimsLookup = updatedClaims.toDictionary(x => x.ClaimId);

                for (let encounter of encounters) {
                    for (let claim of encounter.Model.FacturationNetBillsList) {
                        let updatedClaim = updatedClaimsLookup[claim.ClaimId];
                        if (updatedClaim) {
                            claim.Status = updatedClaim.ClaimStatus;
                            claim.Codes = updatedClaim.Codes;
                            claim.PreAmount = updatedClaim.PreAmount;
                            claim.Amount = updatedClaim.Amount;
                        }
                    }

                    encounter.Model.FacturationNetBillsList = encounter.Model.FacturationNetBillsList
                        .filter(bill => !deletedIds.contains(bill.ClaimId));
                }

                store.NotesInboxModel = store.NotesInboxModel.set("TableData", encounters.toImmutable());
                store.EmitChangeToListeners(); // because this is in response of a server call that did not go through actions
            }
        );
    }

    private async updateExternalBillingClaimStatusesAsync(store: PortfolioStore): Promise<void> {
        const tableData = store.NotesInboxModel.get("TableData").toArray();

        let encounterIds = tableData
            .map(p => p.Model.EncounterId);

        if (!encounterIds.length)
            return;

        // fetch updated invoices
        const claims = await ExternalBillingManager.getClaimsByEncounterIdsAsync(encounterIds);

        if (!claims)
            return;

        const claimsDictionary = claims.toDictionary(p => `${p.BillingProvider}_${p.ProviderClaimId}`);

        for (const tableRow of tableData) {
            const updatedClaims: ExternalBillingClaimDto[] = [];

            for (let claim of tableRow.Model.ExternalBillingClaims) {
                const updatedClaim = claimsDictionary[`${claim.BillingProvider}_${claim.ProviderClaimId}`];

                if (!updatedClaim)
                    continue;

                claim.Status = updatedClaim.Status;
                claim.Codes = updatedClaim.Codes;
                claim.AmountSubmitted = updatedClaim.AmountSubmitted;
                claim.AmountPaid = updatedClaim.AmountPaid;

                updatedClaims.push(claim);
            }

            tableRow.Model.ExternalBillingClaims = updatedClaims;
        }

        store.NotesInboxModel = store.NotesInboxModel.set("TableData", tableData.toImmutable());
        store.EmitChangeToListeners(); // because this is in response of a server call that did not go through actions
    }

    private loadPortalSyncHistory(response: ServerEvent, store: PortfolioStore): void {
        const rawSyncHistory: SharedItemHistoryDto[] = response.Data["SyncHistory"];

        const syncHistory = rawSyncHistory
            .map(p => {
                p.SyncDate = moment(p.SyncDate);
                p.ViewedDate = p.ViewedDate && moment(p.ViewedDate);

                return new PortalSyncHistoryTableItem(p);
            })
            .toImmutable();

        store.portalSyncModel = store.portalSyncModel
            .set("TableData", syncHistory)
            .set("CurrentRowOffset", response.Data["CurrentRowOffset"] as number)
            .set("TotalRows", response.Data["TotalRows"] as number);

        store.PortfolioActions.LoadPortfolioCount();
    }

    private loadSharedItem(response: ServerEvent, store: PortfolioStore): void {
        if (response.Errors.any()) {
            store.portalSyncModel = store.portalSyncModel
                .set("LoadingSharedItem", false);
            return;
        }

        const sharedItem = new SharedItem(response.Data["SharedItem"] as Partial<SharedItem>);
        const linkedItem = response.Data["LinkedItem"] as LinkedItem ?? new LinkedItem();

        const selectedItem = store.portalSyncModel.get("SelectedRow");
        const patientPortalSyncState = selectedItem &&
            selectedItem.Model &&
            selectedItem.Model.Patient &&
            selectedItem.Model.Patient.PortalSyncState;

        const portfolioShareableItem = new PortfolioShareableItem(sharedItem, linkedItem);
        const syncToPortalModel = (Immutable.fromJS({
            LinkedItemType: sharedItem.SharedItemType,
            LinkedItemId: linkedItem.LinkedItemId,
            LinkedItemSiteId: linkedItem.SiteId,
            SendButtonText: cacheStore.GetString("SyncToPatientPortal"),
            DefaultBody: sharedItem.SharedItemType === PortalLinkedItemType.CommunicationHistory ? linkedItem.Title : null,
            DefaultSubject: IsNullOrWhiteSpace(sharedItem.Subject) ? linkedItem.Title : sharedItem.Subject,
            IsSending: false,
            RegistrationStatus: patientPortalSyncState,
            PatientContactId: sharedItem.PatientContactId
        }) as IPortalLinkedItemModel)
            .set("SharedItem", sharedItem)
            .set("LinkedItemModel", portfolioShareableItem)
            .set("LinkedItemMessages", portfolioShareableItem.getSharedItemMessages().toImmutable());

        store.portalSyncModel = store.portalSyncModel
            .set("SyncToPortalModel", syncToPortalModel)
            .set("LoadingSharedItem", false)
            .set("SyncToPortalPopupOpen", true);
    }

    private updateSharedItem(response: ServerEvent, store: PortfolioStore): void {
        let syncToPortalModel = store.portalSyncModel
            .get("SyncToPortalModel")
            .set("IsSending", false);

        if (response.hasErrors()) {
            store.portalSyncModel = store.portalSyncModel
                .set(
                    "SyncToPortalModel",
                    syncToPortalModel.set("ErrorMessage", response.Errors[0].Message)
                );
            return;
        }

        store.portalSyncModel = store.portalSyncModel
            .set("SyncToPortalModel", null)
            .set("SyncToPortalPopupOpen", false);

        store.PortfolioActions.LoadPortalSyncHistory(store.portalSyncModel);
    }

    private loadPreventiveActivities(response: ServerEvent, store: PortfolioStore): void {
        if (response.Errors.any())
            return;

        const items = response.Data["PreventiveActivities"]?.map(p => new PreventiveActivitiesTableItem(p)) || [];

        store.preventiveActivitiesInboxModel = store.preventiveActivitiesInboxModel
            .set("IsSettingsContainerOpen", false)
            .set("TotalQueryPages", response.Data.TotalQueryPages)
            .set("TotalQueryRows", response.Data.TotalQueryRows)
            .set("CurrentPage", response.Data.CurrentPage)
            .set("PreventiveActivities", items);
    }

    private loadPreventiveRecallList(response: ServerEvent, store: PortfolioStore): void {
        if (response.Errors.any())
            return;

        const items: PreventiveRecallListTableItem[] = response.Data.RecallList?.map(p => new PreventiveRecallListTableItem(p)) || [];

        store.preventiveRecallListModel = store.preventiveRecallListModel
            .set("IsSettingsContainerOpen", false)
            .set("TotalQueryPages", response.Data.TotalQueryPages)
            .set("TotalQueryRows", response.Data.TotalQueryRows)
            .set("CurrentPage", response.Data.CurrentPage)
            .set("RecallList", items.toImmutable());
    }

    private loadPreventiveCareNotices(response: ServerEvent, store: PortfolioStore): void {
        if (response.Errors.any())
            return;

        const notices: PreventiveCareNotice[] = response.Data.PreventiveCareNotices?.map(p => new PreventiveCareNotice(p)) || [];

        store.preventiveRecallListModel = store.preventiveRecallListModel
            .set("PreventiveCareNotices", notices)
            .set("CurrentTab", PreventiveRecallTab.CorrespondenceHistory);
        store.Model = store.Model.set("CurrentInbox", PortfolioInboxes.PreventiveRecallList);
    }

    private generateLetters(response: ServerEvent, store: PortfolioStore): void {
        if (response.Errors.any())
            return;

        PrintHelper.ShowPrintPreviewFileName(response.Data.FileName);
        store.PortfolioActions.loadPreventiveRecallList();
    }

    private generateBonusReport(response: ServerEvent, store: PortfolioStore): void {
        if (!response.Errors.any()) {
            PrintHelper.ShowPrintPreviewFileName(response.Data.FileName);
        }

        store.preventiveRecallListModel = store.preventiveRecallListModel
            .set("IsGenerateBonusReportLoading", false);
    }

    private Myle_UploadApssAttachments_ActionRequired(): void {
        globalThis.setTimeout(
            () => {
                LocalEventDispatcher.dispatch(
                    new LocalEvent(LocalOperations.Portfolio.APSS_ActionRequired_Reload)
                );
            },
            50
        );
    }
}

class PortfolioShareableItem implements IPortalShareableItem {
    private readonly sharedItem: SharedItem;
    private readonly linkedItem: LinkedItem;

    constructor(sharedItem: SharedItem, linkedItem: LinkedItem) {
        this.sharedItem = sharedItem;
        this.linkedItem = linkedItem;
    }

    public getMyleObjectType(): MyleObjectType {
        throw "Not Implemented";
    }

    public getSharedItemMessages(): SharedItemMessage[] {
        const sharedItemMessages = this.sharedItem.Messages || [];
        const linkedItemNotes = this.linkedItem.Notes || [];

        const copiedFromIds = sharedItemMessages.map(p => p.CopiedFromId).filter(p => !IsNullOrWhiteSpace(p));
        const unsentNotes = linkedItemNotes.filter(p => !copiedFromIds.contains(p.NoteId));

        const messages = ArrayHelper.SortDynamic(
            sharedItemMessages.concat(PortalHelper.convertLinkedItemNotesToPortalMessage(unsentNotes, this.sharedItem.SharedItemType)),
            "DateCreated",
            true
        );

        messages.forEach(p => {
            p.IsSelected = p.Status == PortalSyncStatus.Pending || p.Status == PortalSyncStatus.Viewed;
        });

        return messages;
    }

    public getSharedItem(): SharedItem {
        return this.sharedItem;
    }

    public getSharedItemId(): string {
        return this.sharedItem.SharedItemId;
    }

    public getGlobalPortalStatus(): PortalSyncStatus {
        return PortalHelper.getGlobalPortalStatus(this.sharedItem, this.sharedItem.Messages);
    }

    public getItemTypeHeader(): string {
        return this.linkedItem.TypeHeader;
    }

    public getItemTitle(): string {
        return this.linkedItem.Title;
    }

    public getDefaultSubject(): string {
        return this.sharedItem && !IsNullOrWhiteSpace(this.sharedItem.Subject)
            ? this.sharedItem.Subject
            : this.getItemTitle();
    }

    public getPortalObjectId(): string {
        throw "Not Implemented";
    }

    public getPortalLinkedItemType(): PortalLinkedItemType {
        return this.sharedItem.SharedItemType;
    }

    public getSharedItemPortalStatus(): PortalSyncStatus {
        return PortalSyncStatus.Unknown;
    }

    public hasBeenModifiedSinceLastShared(): boolean {
        return false;
    }

    public hasBeenSharedOnPreviousEncounter(): boolean {
        return false;
    }
}

export { PortfolioResponseHandler };
